// First-touch signup attribution. Captured once when a visitor first lands on
// the dashboard (typically /register with gclid/UTM forwarded from the landing
// pages) and persisted so it survives the multi-step magic-link / OTP flow.

export interface Attribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  gclid?: string;
  gaClientId?: string;
  referrer?: string;
  landingPath?: string;
}

const STORAGE_KEY = "vms_attribution";

// Extract the GA client_id from the `_ga` cookie. The cookie has the form
// `GA1.1.<clientId>.<ts>` where clientId is itself two dot-segments
// (e.g. `1234567890.1700000000`). We rejoin those last two segments.
function readGaClientId(): string | undefined {
  try {
    const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    if (!match) return undefined;
    const parts = decodeURIComponent(match[1]).split(".");
    if (parts.length < 4) return undefined;
    return parts.slice(-2).join(".");
  } catch {
    return undefined;
  }
}

function clean(obj: Attribution): Attribution {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v != null && v !== ""),
  );
}

// Capture attribution from the current URL + referrer if we haven't already.
// First-touch wins: we never overwrite a previously stored attribution.
export function captureAttribution(): void {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return;

    const params = new URLSearchParams(window.location.search);
    const gclid =
      params.get("gclid") || params.get("gbraid") || params.get("wbraid") || undefined;

    // The landing site forwards the TRUE external referrer + landing path as
    // vms_ref / vms_lp (the browser's own referrer here is just our landing
    // page). Fall back to document.referrer for visitors who hit the app
    // directly without going through a landing page.
    const docReferrer = document.referrer || undefined;
    const referrer =
      params.get("vms_ref") ||
      (docReferrer && !docReferrer.includes(window.location.host) ? docReferrer : undefined);
    const landingPath =
      params.get("vms_lp") || window.location.pathname + window.location.search || undefined;

    const data = clean({
      utmSource: params.get("utm_source") || undefined,
      utmMedium: params.get("utm_medium") || undefined,
      utmCampaign: params.get("utm_campaign") || undefined,
      utmTerm: params.get("utm_term") || undefined,
      utmContent: params.get("utm_content") || undefined,
      gclid,
      gaClientId: readGaClientId(),
      referrer,
      landingPath,
    });

    if (Object.keys(data).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
  } catch {
    // localStorage unavailable (private mode / blocked) — attribution is best-effort.
  }
}

export function getAttribution(): Attribution | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = clean(JSON.parse(raw) as Attribution);
    return Object.keys(parsed).length > 0 ? parsed : undefined;
  } catch {
    return undefined;
  }
}
